import { useQueryClient } from '@tanstack/react-query';
import {
  CircleX,
  Eye,
  History,
  ListChecks,
  MoreHorizontal,
  Play,
  RefreshCw,
  Settings2,
  Trash2,
  Flame,
} from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { PageContainer } from '../app/AppLayout';
import type { Versioned } from '../api/client';
import { useApi } from '../api/context';
import {
  NETWORK_POLL_MS,
  useAddressPlan,
  useNetwork,
  useNetworkActiveRuns,
  useNetworkDns,
  useNetworkKinds,
  useNetworkRuns,
} from '../api/queries';
import type { Network, RunSummary } from '../api/types';
import { isTerminalRunStatus } from '../api/types';
import { driftPresentation } from '../components/presentation';
import { useToast } from '../components/toast';
import { ActionButton, ErrorMessage, Loading, StatusBadge } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { CancelRunButton } from '../runs/CancelRun';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { strings } from '../strings';
import { AddressPlanTab } from './AddressPlanTab';
import { ApplyNetworkDialog } from './ApplyNetworkDialog';
import { DnsTab } from './DnsTab';
import { EntryPointsTab } from './EntryPointsTab';
import { NetworkActivityTab } from './NetworkActivityTab';
import { NetworkSettingsTab } from './NetworkSettingsTab';
import { TrafficTab } from './requests/TrafficTab';
import { AttachDeploymentsDialog } from './page/AttachDeploymentsDialog';
import { AttachmentsTab } from './page/AttachmentsTab';
import { blockingRun, useAllAttachments, useRun, useStartNetworkRun } from './page/hooks';
import { DeleteNetworkDialog, DestroyNetworkDialog } from './page/NetworkDialogs';
import { NetworkOverview } from './page/NetworkOverview';
import { NetworkRunsTab } from './page/NetworkRunsTab';
import { totalAttachments } from './page/topology';
import {
  displayActor,
  NetworkIcon,
  networkRunLabel,
  networkStatusPresentation,
  providerLabel,
  useManageNetworks,
} from './shared';

const s = strings.networks;
const p = s.page;

const TABS = [
  'overview',
  'attachments',
  'address-plan',
  'dns',
  'entry-points',
  'traffic',
  'runs',
  'settings',
  'activity',
] as const;
type Tab = (typeof TABS)[number];
/** URL parameters that belong to one tab; switching tabs drops the others'. */
const TAB_PARAMS: Partial<Record<Tab, readonly string[]>> = {
  overview: ['select'],
  attachments: ['state', 'provider', 'region', 'q'],
  traffic: ['decision', 'enforcement', 'source', 'target', 'needed', 'pair'],
  activity: ['kind', 'actor'],
};
const ALL_TAB_PARAMS = Object.values(TAB_PARAMS).flat();

function tabFrom(value: string | null): Tab {
  return TABS.includes(value as Tab) ? (value as Tab) : 'overview';
}

/** The page's tabs (as the deployment page's): arrow keys, Home and End, one tab in the Tab order. */
function NetworkTabs({
  id,
  active,
  counts,
  warn = {},
  onChange,
}: {
  id: string;
  active: Tab;
  counts: Partial<Record<Tab, number>>;
  /** Tabs whose count is in the warning tone (requests that block traffic on an enforcing network). */
  warn?: Partial<Record<Tab, boolean>>;
  onChange: (tab: Tab) => void;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = TABS.indexOf(active);
    const next =
      event.key === 'ArrowRight'
        ? TABS[(index + 1) % TABS.length]
        : event.key === 'ArrowLeft'
          ? TABS[(index - 1 + TABS.length) % TABS.length]
          : event.key === 'Home'
            ? TABS[0]
            : event.key === 'End'
              ? TABS[TABS.length - 1]
              : null;
    if (!next) return;
    event.preventDefault();
    onChange(next);
    requestAnimationFrame(() => document.getElementById(`${id}-tab-${next}`)?.focus());
  };
  return (
    <div role="tablist" aria-label={p.tabs} className="page-tabs net-tabs">
      {TABS.map((tab) => (
        <button
          key={tab}
          id={`${id}-tab-${tab}`}
          type="button"
          role="tab"
          aria-selected={active === tab}
          aria-controls={`${id}-panel-${tab}`}
          tabIndex={active === tab ? 0 : -1}
          className={`page-tab ${active === tab ? 'is-active' : ''}`}
          onClick={() => onChange(tab)}
          onKeyDown={onKeyDown}
        >
          {p.tab[tab]}
          {counts[tab] !== undefined && (
            <span className={`net-tab-count ${warn[tab] ? 'is-warning' : ''}`}>{counts[tab]}</span>
          )}
        </button>
      ))}
    </div>
  );
}

interface Banner {
  key: string;
  tone: 'danger' | 'warning' | 'info';
  content: ReactNode;
  actions?: ReactNode;
}

/** `/networks/:id` (spec 11 6.15.4, mock n02): the network page with its tabs. */
export function NetworkPage() {
  const { id = '' } = useParams();
  const network = useNetwork(id, { poll: true });
  if (network.error) return <ErrorMessage error={network.error} />;
  if (!network.data) return <Loading />;
  return <NetworkDetail key={network.data.data.id} network={network.data} />;
}

function NetworkDetail({ network: versioned }: { network: Versioned<Network> }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const tabsId = useId();
  const manage = useManageNetworks();
  const n = versioned.data;
  const tab = tabFrom(search.get('tab'));
  const [visitedSettings, setVisitedSettings] = useState(tab === 'settings');
  if (tab === 'settings' && !visitedSettings) setVisitedSettings(true);
  const [dialog, setDialog] = useState<'apply' | 'destroy' | 'delete' | null>(null);
  const [deleteError, setDeleteError] = useState<unknown>(null);
  const [deleting, setDeleting] = useState(false);
  const [checklist, setChecklist] = useState(false);
  const attachOpen = search.get('attach') === '1';

  const poll = n.activeRun || n.queuedRuns > 0 ? NETWORK_POLL_MS : false;
  const activeRuns = useNetworkActiveRuns(n.id, poll);
  const recent = useNetworkRuns(n.id, null, poll);
  const attachments = useAllAttachments(n.id, poll);
  const plan = useAddressPlan(n.id);
  const dns = useNetworkDns(n.id);
  const kinds = useNetworkKinds(n.id);
  const lastApplyRun = useRun(n.lastSuccessfulApply?.runId);
  const { start, busy } = useStartNetworkRun(n);

  // A run that ends (or starts) changes what the page shows: read the network's lists again.
  const runKey = `${n.activeRun?.id ?? ''}/${n.queuedRuns}/${n.attachments.connected}/${n.attachments.failed}`;
  const lastKey = useRef(runKey);
  useEffect(() => {
    if (lastKey.current === runKey) return;
    lastKey.current = runKey;
    void queryClient.invalidateQueries({
      queryKey: ['network', n.id],
      predicate: (query) => query.queryKey.length > 2,
    });
    void queryClient.invalidateQueries({ queryKey: ['runs'] });
  }, [runKey, n.id, queryClient]);

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(search);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    const keep = TAB_PARAMS[next] ?? [];
    for (const key of ALL_TAB_PARAMS) if (!keep.includes(key)) params.delete(key);
    setSearch(params, { replace: true });
  };
  const showFailedAttachments = () => {
    const params = new URLSearchParams();
    params.set('tab', 'attachments');
    params.set('state', 'failed');
    setSearch(params, { replace: true });
  };
  const setAttach = (open: boolean) => {
    const params = new URLSearchParams(search);
    if (open) params.set('attach', '1');
    else params.delete('attach');
    setSearch(params, { replace: true });
  };

  // ---- header
  const topologyLabel =
    kinds.data?.topologies.find((t) => t.id === n.settings.topology.kind)?.label ?? n.settings.topology.kind;
  const where = Object.entries(n.settings.providers)
    .map(([provider, settings]) => `${providerLabel(provider)} ${(settings?.regions ?? []).join(', ')}`)
    .join(' · ');
  const total = totalAttachments(n.attachments);
  const blocking = blockingRun(activeRuns.data?.items);
  const blockedReason = blocking
    ? (blocking.status === 'pending' ? p.runQueued : p.runRunning)(
        n.name,
        s.runKindLower[blocking.kind],
        displayActor(blocking.createdBy),
      )
    : null;
  const nonPending = n.attachments.connected + n.attachments.connecting + n.attachments.failed;
  const roleReason = manage ? null : s.requiresNetworkAdmin;
  const destroyReason =
    roleReason ??
    blockedReason ??
    (n.status === 'new' || n.status === 'destroyed'
      ? p.nothingToDestroy
      : nonPending > 0
        ? p.destroyDetachFirst(nonPending)
        : null);
  const removable = n.status === 'new' || n.status === 'destroyed';
  const deleteReason = roleReason ?? (total > 0 ? p.deleteOnlyNew : null);
  const menu: MenuEntry[] = [
    {
      kind: 'action',
      id: 'getting-started',
      label: p.gettingStarted,
      icon: <ListChecks aria-hidden className="size-4" />,
      onSelect: () => {
        setChecklist(true);
        setTab('overview');
      },
    },
    {
      kind: 'action',
      id: 'destroy',
      label: p.destroy,
      icon: <Flame aria-hidden className="size-4" />,
      disabledReason: destroyReason ?? undefined,
      onSelect: () => setDialog('destroy'),
    },
  ];
  if (removable)
    menu.push({
      kind: 'action',
      id: 'delete',
      label: p.delete,
      icon: <Trash2 aria-hidden className="size-4" />,
      disabledReason: deleteReason ?? undefined,
      onSelect: () => {
        setDeleteError(null);
        setDialog('delete');
      },
    });

  const remove = async () => {
    setDeleting(true);
    try {
      await api.deleteNetwork(n.id, versioned.etag);
      void queryClient.invalidateQueries({ queryKey: ['networks'] });
      queryClient.removeQueries({ queryKey: ['network', n.id] });
      toast.show({ tone: 'success', title: p.deleted(n.name) });
      navigate('/networks');
    } catch (error) {
      setDeleteError(error);
      setDeleting(false);
    }
  };

  // ---- run banner
  const runningRun: RunSummary | undefined = activeRuns.data?.items.find((r) => r.status === 'running');

  // ---- banners (at most two)
  const recentRuns = recent.data?.items ?? [];
  const lastFailed =
    n.status === 'failed'
      ? recentRuns.find(
          (r) => isTerminalRunStatus(r.status) && r.triggeredBy === 'user' && (r.scope ?? 'full') === 'full',
        )
      : undefined;
  const banners: Banner[] = [];
  const problems: { text: ReactNode; action?: ReactNode }[] = [];
  if (n.status === 'failed')
    problems.push({
      text: (
        <>
          <span className="font-semibold">
            {p.lastRunFailed(s.runKindLower[lastFailed?.kind ?? 'apply'], n.name)}
          </span>{' '}
          {lastFailed?.error && (
            <>
              <code className="font-mono">{lastFailed.error.code}</code>: {lastFailed.error.message}
            </>
          )}
        </>
      ),
      action: lastFailed && (
        <Link className="btn btn-sm" to={`/runs/${lastFailed.id}`}>
          {p.openRun}
        </Link>
      ),
    });
  if (n.needsRecovery || n.needsRefresh)
    problems.push({
      text: p.refreshFirst,
      action: (
        <ActionButton
          permission="manage-networks"
          className="btn-sm"
          disabledReason={blockedReason}
          onClick={() => void start({ kind: 'refresh' })}
        >
          {p.refresh}
        </ActionButton>
      ),
    });
  if (n.status === 'destroyed') problems.push({ text: p.destroyed(n.name) });
  if (n.attachments.failed > 0)
    problems.push({
      text: p.failedAttachments(n.attachments.failed),
      action: (
        <button type="button" className="btn btn-sm" onClick={showFailedAttachments}>
          {p.show}
        </button>
      ),
    });
  if (problems.length)
    banners.push({
      key: 'problem',
      tone: n.status === 'failed' || n.attachments.failed > 0 ? 'danger' : 'warning',
      content: problems.map((x, i) => (
        <span key={i} className="block">
          {x.text}
        </span>
      )),
      actions: problems.some((x) => x.action) ? (
        <>
          {problems.map((x, i) => (
            <span key={i}>{x.action}</span>
          ))}
        </>
      ) : null,
    });
  if (n.settingsChangedSinceApply || n.attachments.waitingForApply > 0)
    banners.push({
      key: 'apply',
      tone: 'warning',
      content: (
        <>
          {n.settingsChangedSinceApply && <span className="font-semibold">{p.settingsChanged}</span>}
          {n.settingsChangedSinceApply && n.attachments.waitingForApply > 0 && ' '}
          {n.attachments.waitingForApply > 0 && p.waitForApply(n.attachments.waitingForApply)}
        </>
      ),
      actions: manage ? (
        <ActionButton
          permission="manage-networks"
          className="btn-sm"
          disabledReason={blockedReason}
          icon={<Eye aria-hidden className="size-4" />}
          onClick={() => void start({ kind: 'plan' })}
        >
          {s.actions.planNetwork}
        </ActionButton>
      ) : (
        <span className="text-[12.5px]">{s.askAdminToApply(n.name)}</span>
      ),
    });

  const settingsDestroyReason = destroyReason;

  return (
    <PageContainer full>
      <header className="deployment-header" data-testid="network-header">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">{n.name}</h1>
            <StatusBadge presentation={networkStatusPresentation[n.status]} />
            {n.lastSuccessfulApply && n.drift !== 'none' && (
              <StatusBadge presentation={driftPresentation[n.drift]} />
            )}
            {n.settingsChangedSinceApply && (
              <span className="badge tone-warning">
                <Settings2 aria-hidden className="size-3.5" />
                {s.settingsChanged}
              </span>
            )}
          </div>
          <p
            className="mt-1 flex flex-wrap items-center gap-x-1 text-sm text-fg-muted"
            data-testid="network-context"
          >
            <NetworkIcon aria-hidden className="size-3.5" />
            <span>{topologyLabel}</span>
            {' · '}
            <span>{where}</span>
            {' · '}
            {n.lastSuccessfulApply ? (
              <span title={formatDateTime(n.lastSuccessfulApply.completedAt)}>
                {p.contextApplied(
                  formatRelative(n.lastSuccessfulApply.completedAt),
                  lastApplyRun.data ? displayActor(lastApplyRun.data.createdBy) : null,
                )}
              </span>
            ) : (
              <span>{p.notApplied}</span>
            )}
            {' · '}
            <span>{p.attachments(total)}</span>
          </p>
        </div>
        <div className="deployment-actions">
          <ActionButton
            permission="manage-networks"
            disabledReason={blockedReason}
            disabled={busy}
            icon={<Eye aria-hidden className="size-4" />}
            onClick={() => void start({ kind: 'plan' })}
          >
            {p.plan}
          </ActionButton>
          <ActionButton
            permission="manage-networks"
            variant="primary"
            disabledReason={blockedReason}
            icon={<Play aria-hidden className="size-4" />}
            onClick={() => setDialog('apply')}
          >
            {p.apply}
          </ActionButton>
          <ActionButton
            permission="manage-networks"
            disabledReason={blockedReason ?? (n.lastSuccessfulApply ? null : p.nothingToRefresh)}
            disabled={busy}
            icon={<RefreshCw aria-hidden className="size-4" />}
            onClick={() => void start({ kind: 'refresh' })}
          >
            {p.refresh}
          </ActionButton>
          <MenuButton
            buttonContent={<MoreHorizontal aria-hidden className="size-4" />}
            buttonLabel={p.moreActions(n.name)}
            buttonTitle={p.moreActions(n.name)}
            buttonClassName="btn btn-icon"
            menuLabel={p.moreActions(n.name)}
            entries={menu}
            placement="below-end"
          />
        </div>
      </header>

      {runningRun && (
        <div className="callout callout-info net-banner mb-3" role="status" data-testid="network-run-banner">
          <History aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="font-semibold">{p.runBanner(networkRunLabel(runningRun))}</span>
            {runningRun.progress && runningRun.progress.total > 0 && (
              <>
                {' '}
                · {runningRun.progress.completed}/{runningRun.progress.total}
              </>
            )}
            {' · '}
            {p.runStarted(
              formatRelative(runningRun.startedAt ?? runningRun.createdAt),
              displayActor(runningRun.createdBy),
            )}
            {n.queuedRuns > 0 && (
              <>
                {' · '}
                <button type="button" className="link" onClick={() => setTab('runs')}>
                  {p.runsQueued(n.queuedRuns)}
                </button>
              </>
            )}
          </span>
          <span className="flex shrink-0 gap-2">
            <Link className="btn btn-sm" to={`/runs/${runningRun.id}`}>
              {p.openRun}
            </Link>
            <CancelRunButton run={runningRun} label={p.cancel} small />
          </span>
        </div>
      )}

      {banners.length > 0 && (
        <div aria-label={p.banners} role="group" className="mb-3 space-y-2">
          {banners.slice(0, 2).map((banner) => (
            <div
              key={banner.key}
              className={`callout callout-${banner.tone} net-banner`}
              data-testid={`network-banner-${banner.key}`}
            >
              {banner.tone === 'danger' ? (
                <CircleX aria-hidden className="size-4 shrink-0" />
              ) : (
                <Settings2 aria-hidden className="size-4 shrink-0" />
              )}
              <div className="min-w-0 flex-1">{banner.content}</div>
              {banner.actions && <div className="flex shrink-0 flex-wrap gap-2">{banner.actions}</div>}
            </div>
          ))}
        </div>
      )}

      <NetworkTabs
        id={tabsId}
        active={tab}
        counts={{
          attachments: total,
          'entry-points': n.settings.entryPoints.length,
          ...(n.requests.pending > 0 ? { traffic: n.requests.pending } : {}),
        }}
        warn={{ traffic: !!n.requests.enforcing && n.requests.pending > 0 }}
        onChange={setTab}
      />
      {tab === 'overview' && (
        <div
          id={`${tabsId}-panel-overview`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-overview`}
          className="page-tabpanel"
        >
          <NetworkOverview
            network={n}
            attachments={attachments.data}
            attachmentsError={attachments.error}
            plan={plan.data}
            dns={dns.data}
            poll={poll}
            showChecklist={checklist}
            busy={busy}
            onCloseChecklist={() => setChecklist(false)}
            onPlan={() => void start({ kind: 'plan' })}
            onAttach={() => setAttach(true)}
          />
        </div>
      )}
      {tab === 'attachments' && (
        <div
          id={`${tabsId}-panel-attachments`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-attachments`}
          className="page-tabpanel"
        >
          <AttachmentsTab network={n} onAttach={() => setAttach(true)} poll={poll} />
        </div>
      )}
      {tab === 'address-plan' && (
        <div
          id={`${tabsId}-panel-address-plan`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-address-plan`}
          className="page-tabpanel"
        >
          <AddressPlanTab network={versioned} />
        </div>
      )}
      {tab === 'dns' && (
        <div
          id={`${tabsId}-panel-dns`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-dns`}
          className="page-tabpanel"
        >
          <DnsTab network={versioned} />
        </div>
      )}
      {tab === 'entry-points' && (
        <div
          id={`${tabsId}-panel-entry-points`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-entry-points`}
          className="page-tabpanel"
        >
          <EntryPointsTab network={versioned} />
        </div>
      )}
      {tab === 'traffic' && (
        <div
          id={`${tabsId}-panel-traffic`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-traffic`}
          className="page-tabpanel"
        >
          <TrafficTab network={versioned} />
        </div>
      )}
      {tab === 'runs' && (
        <div
          id={`${tabsId}-panel-runs`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-runs`}
          className="page-tabpanel"
        >
          <NetworkRunsTab network={n} poll={poll} />
        </div>
      )}
      {visitedSettings && (
        <div
          id={`${tabsId}-panel-settings`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-settings`}
          className="page-tabpanel"
          hidden={tab !== 'settings'}
        >
          <NetworkSettingsTab
            network={versioned}
            onDestroy={() => setDialog('destroy')}
            onDelete={() => {
              setDeleteError(null);
              setDialog('delete');
            }}
            destroyDisabledReason={settingsDestroyReason}
            deleteDisabledReason={removable ? deleteReason : p.deleteOnlyNew}
          />
        </div>
      )}
      {tab === 'activity' && (
        <div
          id={`${tabsId}-panel-activity`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-activity`}
          className="page-tabpanel"
        >
          <NetworkActivityTab network={versioned} />
        </div>
      )}

      {dialog === 'apply' && <ApplyNetworkDialog network={n} onClose={() => setDialog(null)} />}
      {dialog === 'destroy' && (
        <DestroyNetworkDialog
          network={n}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={() => void start({ kind: 'destroy' }).then(() => setDialog(null))}
        />
      )}
      {dialog === 'delete' && (
        <DeleteNetworkDialog
          network={n}
          busy={deleting}
          error={deleteError}
          onClose={() => setDialog(null)}
          onConfirm={() => void remove()}
          onShowAttachments={() => {
            setDialog(null);
            setTab('attachments');
          }}
        />
      )}
      {attachOpen && <AttachDeploymentsDialog network={n} onClose={() => setAttach(false)} />}
    </PageContainer>
  );
}
